import { useEffect, useMemo } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { $getRoot } from 'lexical';
import { $stripAllLinks } from './docTransforms';
import {
  SCRIPT_DOC_NAMESPACE,
  SCRIPT_DOC_NODES,
  scriptDocTheme,
  SCRIPT_TITLE_FIRST_CHILD_CLASSES,
  type ScriptDoc,
} from './config';

export interface ScriptDocRendererProps {
  /** The script's native document (Lexical SerializedEditorState). */
  doc: ScriptDoc;
  /** Fired after the read-only editor has parsed the doc and settled its DOM.
   *  Consumers (e.g. the comment overlay) use it to (re)run passes that must
   *  walk the rendered text nodes once they exist. Re-fires when `doc` changes. */
  onReady?: () => void;
}

/** Re-parses the doc into the editor in place when it changes, so a new version
 *  updates without remounting the whole Lexical instance. Guards the parse so a
 *  malformed doc degrades instead of crashing the panel. */
function DocState({ serialized }: { serialized: string }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    try {
      editor.setEditorState(editor.parseEditorState(serialized));
      // This renderer has no DocHygienePlugin (no editing surface to
      // protect) — sweep for links explicitly instead of trusting the
      // stored bytes are already clean.
      editor.update(
        () => {
          $stripAllLinks($getRoot());
        },
        { discrete: true },
      );
    } catch (e) {
      console.error('ScriptDocRenderer: failed to parse doc', e);
    }
  }, [editor, serialized]);
  return null;
}

/** Renders a script doc read-only with the same nodes/theme the editor used. */
export function ScriptDocRenderer({ doc, onReady }: ScriptDocRendererProps) {
  const serialized = useMemo(() => JSON.stringify(doc), [doc]);

  // Signal readiness after the doc is (re)serialized and the DocState child
  // effect below has applied it — parent effects run after child effects, so
  // by here the read-only editor has the new state and its DOM has rendered.
  useEffect(() => {
    onReady?.();
  }, [onReady, serialized]);

  const initialConfig = useMemo(
    () => ({
      namespace: SCRIPT_DOC_NAMESPACE,
      nodes: [...SCRIPT_DOC_NODES],
      theme: scriptDocTheme,
      editable: false,
      // Log, don't rethrow: a bad doc must degrade, not crash the review/share
      // panel (this parse runs during init, outside LexicalErrorBoundary).
      onError: (e: Error) => {
        console.error('ScriptDocRenderer: render error', e);
      },
    }),
    [],
  );

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <RichTextPlugin
        contentEditable={
          <ContentEditable className={`leading-relaxed text-foreground outline-none ${SCRIPT_TITLE_FIRST_CHILD_CLASSES}`} />
        }
        placeholder={null}
        ErrorBoundary={LexicalErrorBoundary}
      />
      <DocState serialized={serialized} />
    </LexicalComposer>
  );
}
